import {
  createJsonBoundaryScanner,
  getBlockContent,
  isStructuredContentType,
  type StreamingBlock,
  shouldSkipInTaskOperations,
} from "@storytell/streaming";

/**
 * Safely parse JSON content that might be malformed or concatenated.
 * Handles cases where multiple JSON objects are concatenated together
 * (e.g., `{"type":"chart",...}{"type":"chart",...}`).
 *
 * @param content - The string content to parse
 * @returns The parsed JSON object or null if parsing fails
 */
export function safeParseBlockJson(content: string | undefined | null): unknown {
  if (!content) return null;

  // JSON.parse already tolerates surrounding whitespace, and block payloads run
  // to hundreds of KB — trimming up front copies the whole string on every call,
  // including the calls that were only ever going to fail.
  try {
    return JSON.parse(content);
  } catch {
    // If normal parse fails, try to extract first JSON object
  }

  // Concatenated or still-streaming payload: parse only the first object. The
  // scanner owns the brace-matching rules so classification and parsing can
  // never disagree about where that object ends.
  const scanner = createJsonBoundaryScanner();
  if (scanner.push(content) !== "closed") {
    return null;
  }

  try {
    return JSON.parse(content.slice(0, scanner.endOffset));
  } catch {
    return null;
  }
}

/**
 * Resolve the discriminator `type` of a tool-result block from its JSON payload.
 *
 * Pass `streamingContent` to make the result update during live streaming: the
 * granular store's per-block content is non-reactive, so a caller that wants the
 * type to resolve before block completion must supply the content it has tracked
 * via {@link subscribeToBlockDeltas}. Without it, the type only resolves once
 * `finalContent` is set at block completion.
 */
export function blockJsonType(block: StreamingBlock, streamingContent?: string): string | undefined {
  if (block.finalContent?.type === "text" && block.finalContent.text) {
    const parsed = safeParseBlockJson(block.finalContent.text) as { type?: string } | null;
    if (parsed?.type) return parsed.type;
  }
  const content = streamingContent ?? getBlockContent(block.blockId);
  if (content) {
    const parsed = safeParseBlockJson(content) as { type?: string } | null;
    if (parsed?.type) return parsed.type;
  }
  return undefined;
}

/**
 * Check if parsed content is a known structured type that has a dedicated renderer.
 * Uses the content type registry to determine if a type has a dedicated renderer.
 */
export function isStructuredContent(parsed: unknown): parsed is { type: string } {
  if (!parsed || typeof parsed !== "object") return false;
  const obj = parsed as { type?: string };
  return typeof obj.type === "string" && isStructuredContentType(obj.type);
}

const checkContent = (content: string | undefined): boolean | null => {
  if (!content) return null;

  const trimmed = content.trim();
  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed) as { type?: string };
      return shouldSkipInTaskOperations(parsed.type);
    } catch {
      // Incomplete JSON during streaming - skip to prevent leak
      return true;
    }
  }
  return null;
};

/**
 * Determines if a block should be skipped from rendering based on its content.
 * Returns true if the block contains structured JSON (task_result, summary, progress)
 * or if it contains incomplete JSON (to prevent leaking raw text during streaming).
 *
 * This is used by TaskOperationRenderer and NestedOperationRenderer to avoid
 * rendering blocks that are either:
 * 1. Already displayed via dedicated UI (task results, summaries, progress)
 * 2. Still being streamed and contain incomplete JSON
 */
export function shouldSkipStructuredBlock(block: {
  finalContent?: { type: string; text?: string };
  blockId: string;
  title?: string;
}): boolean {
  // Always skip Summary blocks by title
  if (block.title === "Summary") {
    return true;
  }

  // Check finalContent first (completed blocks)
  if (block.finalContent?.type === "text") {
    const result = checkContent(block.finalContent.text);
    if (result !== null) return result;
  }

  // Check streaming content
  const streamingContent = getBlockContent(block.blockId);
  const result = checkContent(streamingContent);
  if (result !== null) return result;

  return false;
}
